/**
 * Servicio para conectar con APIs de Márgenes del Backend
 * REPLICAR ESTRUCTURA EXACTA DE analytics.service.ts
 */

import { apiClient } from "./apiClient";

// Interfaces de respuesta - REPLICAR ESTRUCTURA DE analytics.service.ts
export interface MonthlyMarginsResponse {
  period: string;
  month: string;
  year: number;
  avgMarginPercentage: number;
  totalMarginAmount: number;
  totalProducts: number;
  totalStores: number;
  marginGrowth?: number;
  previousMonthMargin?: number;
  totalRevenue: number;
  totalCost: number;
  totalDiscounts: number;
  totalCommissions?: number; // Comisiones totales del mes
  realMarginAmount?: number; // Margen real después de comisiones
  realMarginPercentage?: number; // Porcentaje real después de comisiones
  // Campos del Bloque 6 refactor_margenes — populados por backend desde las
  // CTEs reusables (sales-trends). Backward-compat: componentes que no los
  // consumen los ignoran (TS structural typing).
  lastDataDate?: string; // ISO YYYY-MM-DD del dato más reciente disponible
  isCurrentMonth?: boolean; // true si coincide con mes calendario actual
  posRevenue?: number; // Revenue canal POS (pos_order_line)
  posMarginAmount?: number; // Margen canal POS
  soRevenue?: number; // Revenue canal vendedores (sale_order_line)
  soMarginAmount?: number; // Margen canal vendedores
  productsWithoutCost?: number; // Productos con standard_price=0 que vendieron
  inactiveStoresIncluded?: number; // Tiendas active=false con ventas históricas
  // Campos del Bloque 9 plan dashboard_ux_ventas_margenes 2026-05-21 — backward-compat
  // (frontend ignora si backend aún no los expone; populados cuando B9 cierre).
  previousYearMarginPercentage?: number;
  previousYearMarginAmount?: number;
  projectedMarginPercentage?: number;
  daysElapsed?: number;
  daysInMonth?: number;
}

export interface TopMarginProduct {
  productId: string;
  productName: string;
  storeName: string;
  marginPercentage: number;
  marginAmount: number;
  costPrice: number;
  sellingPrice: number;
  quantitySold: number;
  ranking: number;
  growthPercentage?: number;
  totalDiscounts: number;
  totalCommissions?: number; // Comisión calculada para productos de vendedores
  realMarginAmount?: number; // Margen real después de comisiones
  realMarginPercentage?: number; // Porcentaje real después de comisiones
}

export interface StoreMarginMetrics {
  storeName: string;
  storeType?: string; // Tipo de tienda: 'pos', 'store_category', 'no_category'
  avgMarginPercentage: number;
  totalMarginAmount: number;
  totalProducts: number;
  ranking: number;
  growthPercentage: number;
  bestMarginProduct?: string;
  worstMarginProduct?: string;
  totalRevenue: number;
  totalCost: number;
  totalDiscounts: number;
  directDiscounts?: number; // Descuentos directos (discount%)
  priceListDiscounts?: number; // Descuentos por lista de precios
  returnsAmount?: number; // Devoluciones / notas de crédito
  totalCommissions?: number; // Comisión calculada solo para vendedores
  realMarginAmount?: number; // Margen real después de comisiones
  realMarginPercentage?: number; // Porcentaje real después de comisiones
  totalPosTerminals?: number;
  dataSources?: string;
  // Campos del Bloque 6 refactor_margenes — backward-compat. Por tienda son
  // generalmente undefined; el backend los puebla agregado a nivel mensual,
  // no por tienda individual. Se declaran por consistencia tipológica con
  // el shape mensual y para que un futuro endpoint per-store pueda
  // populárlos sin breaking change.
  posRevenue?: number;
  posMarginAmount?: number;
  soRevenue?: number;
  soMarginAmount?: number;
  productsWithoutCost?: number;
  inactiveStoresIncluded?: number;
}

export interface MarginsLevel1Metrics {
  monthlyData: MonthlyMarginsResponse[];
  summary: {
    totalMarginAmount: number;
    avgMarginPercentage: number;
    totalProducts: number;
    totalStores: number;
    bestMonth: string;
    worstMonth: string;
    yearOverYearGrowth: number;
    // Campos del Bloque 6 refactor_margenes — agregados del mes actual
    // propagados al summary para acceso rápido sin recorrer monthlyData.
    lastDataDate?: string;
    isCurrentMonth?: boolean;
    posRevenue?: number;
    soRevenue?: number;
    productsWithoutCost?: number;
    inactiveStoresIncluded?: number;
  };
  topStores: StoreMarginMetrics[];
  topProductsCurrentMonth: TopMarginProduct[];
}

/**
 * Servicio de Márgenes - COPIAR ESTRUCTURA DE AnalyticsService
 */
class MarginsService {
  /**
   * Obtener métricas principales de márgenes (Level 1).
   * Bloque 10 plan dashboard_ux_ventas_margenes 2026-05-21: acepta opts opcional
   * con startMonth/endMonth/storeName. Backward-compat — sin opts el endpoint
   * usa default (últimos 4 meses sin enriquecimiento YoY).
   */
  static async getMarginsLevel1Metrics(opts?: {
    startMonth?: string | null;
    endMonth?: string | null;
    storeName?: string | null;
  }): Promise<any> {
    const params: Record<string, string> = {};
    if (opts?.startMonth) params["startMonth"] = opts.startMonth;
    if (opts?.endMonth) params["endMonth"] = opts.endMonth;
    if (opts?.storeName) params["storeName"] = opts.storeName;
    const response: any = await apiClient.get("/margins/level1-metrics", {
      params,
    });
    // Backend devuelve { status: "success", data: {...}, message: "..." }
    return response.data.data;
  }

  /**
   * Obtiene datos específicos de un mes - IGUAL QUE getMonthlyData
   */
  static async getMonthlyMarginData(year: number, month: number): Promise<any> {
    const response = await apiClient.get(`/margins/monthly/${year}/${month}`);
    return response.data;
  }

  /**
   * Obtiene análisis de MercadoLibre con márgenes reales
   */
  static async getMercadoLibreAnalysis(url: string): Promise<any> {
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Obtiene el ratio global margen/costo de los últimos N meses (excluye mes actual por defecto)
   */
  static async getGlobalMarginPerCostRatio(
    months: number = 3,
    excludeCurrent: boolean = true,
  ): Promise<{
    ratio: number;
    monthsUsed: string[];
    totals: {
      sales: number;
      costs: number;
      discounts: number;
      commissions: number;
      margin: number;
    };
  }> {
    const response = await apiClient.get(`/margins/margin-ratio`, {
      params: { months, excludeCurrent },
    });
    const data = response.data as {
      ratio: number;
      monthsUsed: string[];
      totals: {
        sales: number;
        costs: number;
        discounts: number;
        commissions: number;
        margin: number;
      };
    };
    return data;
  }

  /**
   * HELPERS - REUTILIZAR de AnalyticsService
   */

  /**
   * Formatea porcentaje de margen - IGUAL QUE formatPercentage
   */
  static formatMarginPercentage(value: number): string {
    if (value === undefined || value === null || isNaN(value)) return "0%";
    return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
  }

  /**
   * Formatea dinero - REUTILIZAR EXACTAMENTE de AnalyticsService
   */
  static formatCurrency(value: number): string {
    if (value === undefined || value === null || isNaN(value)) return "$0";
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  /**
   * Formatea números - REUTILIZAR EXACTAMENTE de AnalyticsService
   */
  static formatNumber(value: number): string {
    if (value === undefined || value === null || isNaN(value)) return "0";
    return new Intl.NumberFormat("es-MX").format(value);
  }

  /**
   * Obtiene color según el margen
   */
  static getMarginColor(percentage: number): "success" | "warning" | "error" {
    if (percentage >= 30) return "success"; // Verde para márgenes > 30%
    if (percentage >= 15) return "warning"; // Naranja para márgenes 15-30%
    return "error"; // Rojo para márgenes < 15%
  }

  /**
   * Calcula salud del margen
   */
  static calculateMarginHealth(percentage: number): string {
    if (percentage >= 50) return "Excelente";
    if (percentage >= 30) return "Bueno";
    if (percentage >= 15) return "Regular";
    if (percentage >= 5) return "Bajo";
    return "Crítico";
  }

  /**
   * Extrae nombre del producto - MANEJAR OBJETOS MULTIIDIOMA DE ODOO
   */
  static extractProductName(fullName: string | object): string {
    if (!fullName) return "Producto sin nombre";

    // Si es un objeto (multiidioma de Odoo), extraer el valor en español o inglés
    if (typeof fullName === "object" && fullName !== null) {
      const nameObj = fullName as any;
      const productName =
        nameObj.es_MX ||
        nameObj.en_US ||
        Object.values(nameObj)[0] ||
        "Producto sin nombre";

      // Limitar longitud para display en tablas
      if (productName.length > 50) {
        return productName.substring(0, 47) + "...";
      }
      return productName;
    }

    // Si es string directo
    if (typeof fullName === "string") {
      if (fullName.length > 50) {
        return fullName.substring(0, 47) + "...";
      }
      return fullName;
    }

    return "Producto sin nombre";
  }

  /**
   * Calcula categoría de margen basada en porcentaje
   */
  static getMarginCategory(percentage: number): string {
    if (percentage >= 50) return "Premium";
    if (percentage >= 30) return "Alto";
    if (percentage >= 15) return "Estándar";
    if (percentage >= 5) return "Bajo";
    return "Crítico";
  }

  /**
   * Calcula riesgo del producto basado en margen
   */
  static getProductRisk(
    marginPercentage: number,
    quantitySold: number,
  ): "low" | "medium" | "high" {
    if (marginPercentage >= 20 && quantitySold > 5) return "low";
    if (marginPercentage >= 10 || quantitySold > 10) return "medium";
    return "high";
  }
}

export default MarginsService;
